import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import { useIdleTimer } from "../../hooks/useIdleTimer";

const ROUTE_ACTIVE_TAB = {
  "/dashboard":          "Dashboard",
  "/kyc":                "Kyc",
  "/profile":            "Profile",
  "/wallet":             "Wallet",
  "/myips":              "My Ip's",
  "/services":           "Services",
  "/my-service":         "Service Wise Ledger Report",
  "/activity":           "Activity",
  "/gst-invoices":       "GST Invoice",
  "/complaints":         "Complaints",
  "/fund-request":       "Fund Request",
  "/api-key":            "API Key",
  "/whitelisted-banks":  "Whitelisted Banks",
  "/reports":            "Reports",
  "/transactions":       "Transaction",
  "/transaction-details":"Transaction",
  "/onboard":            "Kyc",
};

export default function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [contentLoading, setContentLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // ✅ Use ref to prevent multiple logout calls
  const isLoggingOut = useRef(false);

  // ✅ State for token check
  const [hasToken, setHasToken] = useState(() => {
    return !!localStorage.getItem("token");
  });

  const activeTab = ROUTE_ACTIVE_TAB[pathname] || "Dashboard";

  // ✅ Complete logout handler with API key cleanup
  const handleLogout = useCallback(() => {
    // Prevent multiple logout calls
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;

    console.log("🚪 Logging out... Clearing all data");

    // Clear all storage (API key, token, user data, etc.)
    localStorage.removeItem("token");
    localStorage.removeItem("api_key");
    localStorage.removeItem("user");
    localStorage.removeItem("last_activity");
    localStorage.removeItem("logout");
    localStorage.removeItem("tab_closed_at");
    
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("api_key");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("last_activity");
    sessionStorage.removeItem("tab_closed_at");
    sessionStorage.clear();
    
    setHasToken(false);
    
    // Navigate with replace to prevent back button issues
    navigate("/login", { replace: true, state: { sessionExpired: true } });
    
    // Reset after navigation
    setTimeout(() => {
      isLoggingOut.current = false;
    }, 500);
  }, [navigate]);

  // ✅ Sync token across tabs
  useEffect(() => {
    const syncToken = (e) => {
      if (e.key === "token" || e.key === "logout" || e.key === "api_key") {
        const newToken = localStorage.getItem("token");
        const newApiKey = localStorage.getItem("api_key");
        setHasToken(!!(newToken || newApiKey));
        
        // If token/api_key removed, logout
        if (!newToken && !newApiKey && !isLoggingOut.current) {
          handleLogout();
        }
      }
    };
    
    window.addEventListener("storage", syncToken);
    return () => window.removeEventListener("storage", syncToken);
  }, [handleLogout]);

  // ✅ Check token/api_key on pathname change
  useEffect(() => {
    const token = localStorage.getItem("token");
    const apiKey = localStorage.getItem("api_key");
    const hasValidSession = !!(token || apiKey);
    
    setHasToken(hasValidSession);
    
    // If no session and not logging out, redirect
    if (!hasValidSession && !isLoggingOut.current && pathname !== "/login") {
      handleLogout();
    }
  }, [pathname, handleLogout]);

  // ✅ Check for session expired flag on load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_expired') === 'true') {
      // Clear any remaining data
      handleLogout();
    }
  }, []);

  // ✅ Idle timer - only when user has token/api_key
  const { warning, countdown, stayActive } = useIdleTimer(
    hasToken ? handleLogout : null
  );

  // ✅ Content loading with cleanup
  useEffect(() => {
    setContentLoading(true);
    const timer = setTimeout(() => {
      setContentLoading(false);
    }, 80);
    
    return () => {
      clearTimeout(timer);
      setContentLoading(false);
    };
  }, [pathname]);

  return (
    <div className="flex h-screen font-sans overflow-hidden bg-[#F8F9FA]">
      {/* Idle Warning Modal */}
      {warning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-7 w-full max-w-sm text-center animate-in fade-in zoom-in duration-200">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#EA8534" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h3 className="text-[18px] font-bold text-[#34094D] mb-2">Session Expiring Soon</h3>
            <p className="text-sm text-gray-500 mb-1">You've been inactive for 5 minutes.</p>
            <p className="text-sm text-gray-500 mb-5">
              Logging out in{" "}
              <span className="font-bold text-red-500 text-base animate-pulse">{countdown}s</span>
            </p>
            <button
              onClick={stayActive}
              className="w-full py-2.5 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-[#EA8534] to-[#A0111A] hover:opacity-90 transition-all hover:scale-105 active:scale-95 border-none cursor-pointer"
            >
              Stay Logged In
            </button>
          </div>
        </div>
      )}

      <Sidebar 
        activeTab={activeTab} 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
      />
      
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-[260px] transition-all duration-300">
        <Navbar onMenuClick={() => setSidebarOpen(true)} />
        
        <div className="flex-1 overflow-y-auto relative bg-gradient-to-br from-gray-50 to-gray-100">
          <Outlet />
          
          {contentLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm pointer-events-none z-50">
              <div className="w-8 h-8 border-[3px] border-[#4B2364] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}